import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiGet } from "./api";

export interface PublicConfig {
  currency: string;
  paypalClientId: string;
  turnstileSiteKey: string | null;
  contactConfigured: boolean;
  stripeConfigured: boolean;
  paypalConfigured: boolean;
}

interface PublicConfigContextValue {
  config: PublicConfig | null;
  loading: boolean;
  error: string | null;
}

const PublicConfigContext = createContext<PublicConfigContextValue>({
  config: null,
  loading: true,
  error: null,
});

export const PublicConfigProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<PublicConfigContextValue>({
    config: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let active = true;

    apiGet<{ ok: true; data: PublicConfig }>("/api/public-config")
      .then((payload) => {
        if (!active) {
          return;
        }

        setState({
          config: payload.data,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        setState({
          config: null,
          loading: false,
          error: error instanceof Error ? error.message : "Configuration could not be loaded.",
        });
      });

    return () => {
      active = false;
    };
  }, []);

  return <PublicConfigContext.Provider value={state}>{children}</PublicConfigContext.Provider>;
};

export const usePublicConfig = () => useContext(PublicConfigContext);
